import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Laptop, ShieldOff } from "lucide-react";
import type { JSX } from "react";
import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@mailflare/ui";

import { queryKeys } from "@/lib/query-keys";
import { revokeAllSessions, revokeSession } from "@/features/auth/api";
import { useSessions } from "@/features/auth/hooks";

export function SessionsSection(): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sessions = useSessions();

  const revoke = useMutation({
    mutationFn: (id: string) => revokeSession(id),
    onSuccess: () => {
      toast({ title: "Session revoked", variant: "success" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });

  const revokeAll = useMutation({
    mutationFn: revokeAllSessions,
    onSuccess: (result) => {
      toast({ title: `Revoked ${result.revoked} session(s)`, variant: "success" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    },
  });

  if (sessions.isLoading) {
    return (
      <div role="status" aria-label="Loading sessions" className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (sessions.isError) {
    return <ErrorState message="Sessions could not be loaded." />;
  }

  const items = sessions.data ?? [];

  return (
    <section aria-labelledby="sessions-heading">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2 id="sessions-heading">Active sessions</h2>
          </CardTitle>
          <CardAction>
            <Button
              variant="outline"
              size="sm"
              onClick={() => revokeAll.mutate()}
              disabled={revokeAll.isPending || items.length === 0}
            >
              <ShieldOff aria-hidden className="size-4" />
              Sign out everywhere
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <EmptyState
              icon={Laptop}
              title="No active sessions"
              description="Sessions appear here once you sign in on a device."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>IP / Expiry</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <span className="flex items-center gap-2">
                        <Laptop aria-hidden className="text-muted-foreground size-4 shrink-0" />
                        <span className="truncate font-medium">
                          {session.userAgent ?? "Unknown device"}
                        </span>
                        {session.current ? <Badge variant="secondary">This device</Badge> : null}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {session.ip ?? "unknown IP"} · expires {session.expiresAt}
                    </TableCell>
                    <TableCell className="text-right">
                      {!session.current ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => revoke.mutate(session.id)}
                          disabled={revoke.isPending}
                        >
                          Revoke
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
